import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Search, Trash2, Loader2, RotateCcw } from "lucide-react";
import { api, faDigits, faNum, fmtDate } from "../lib/api";
import { Pagination } from "./Inquiries";

export default function RefreshLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [keepModal, setKeepModal] = useState(false);
  const [keepN, setKeepN] = useState(50);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/refresh-logs", { params: { page, page_size: 20, success, search } });
      setItems(data.items || []); setTotal(data.total || 0);
    } catch { toast.error("خطا"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, success]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(total / 20));

  const deleteAll = async () => {
    if (!window.confirm("همه لاگ‌های رفرش حذف شوند؟")) return;
    await api.delete("/admin/refresh-logs"); toast.success("پاک شد"); load();
  };
  const keepLast = async () => {
    await api.post("/admin/refresh-logs/keep-last", { n: parseInt(keepN) });
    toast.success(`${faDigits(keepN)} رکورد آخر نگه داشته شد`);
    setKeepModal(false); load();
  };

  return (
    <div data-testid="refresh-logs-page" className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">لاگ رفرش / لاگین</h1>
          <p className="text-sm text-slate-500 mt-1">مجموع: <b className="tabular-nums">{faNum(total)}</b> رکورد</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="refresh-logs-keep-button" onClick={() => setKeepModal(true)} className="bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 px-4 py-2.5 rounded-xl flex items-center gap-2"><RotateCcw size={16} /> نگهداری N آخر</button>
          <button data-testid="refresh-logs-delete-all-button" onClick={deleteAll} className="bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 px-4 py-2.5 rounded-xl flex items-center gap-2"><Trash2 size={16} /> حذف همه</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input data-testid="refresh-logs-search" placeholder="جستجو در پیام / اکانت / کد ملی" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
        </div>
        <select data-testid="refresh-logs-success-filter" value={success} onChange={e => setSuccess(e.target.value)} className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white">
          <option value="">همه</option>
          <option value="true">موفق</option>
          <option value="false">ناموفق</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="bg-slate-50 text-slate-600 font-medium">
              <tr>
                <th className="py-3 px-4 border-b">زمان</th>
                <th className="py-3 px-4 border-b">اکانت</th>
                <th className="py-3 px-4 border-b">کد ملی</th>
                <th className="py-3 px-4 border-b">نتیجه</th>
                <th className="py-3 px-4 border-b">مرحله</th>
                <th className="py-3 px-4 border-b">کد خطا</th>
                <th className="py-3 px-4 border-b">پیام</th>
                <th className="py-3 px-4 border-b">مدت</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="animate-spin inline" /></td></tr>
                : items.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-slate-400">هیچ رکوردی یافت نشد</td></tr>
                : items.map((r, i) => (
                <motion.tr key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.015 }} className="row-hover border-b border-slate-100 last:border-0">
                  <td className="py-3 px-4 text-xs">{fmtDate(r.ts)}</td>
                  <td className="py-3 px-4 font-mono text-xs">{r.account_id || "-"}</td>
                  <td className="py-3 px-4 tabular-nums text-xs">{faDigits(r.national_code)}</td>
                  <td className="py-3 px-4"><span className={`text-xs px-2 py-1 rounded-full border ${r.success ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>{r.success ? "موفق" : "ناموفق"}</span></td>
                  <td className="py-3 px-4 text-xs">{r.step || "-"}</td>
                  <td className="py-3 px-4 text-xs">{r.error_code || "-"}</td>
                  <td className="py-3 px-4 text-xs max-w-md truncate" title={r.message}>{r.message}</td>
                  <td className="py-3 px-4 tabular-nums text-xs">{faNum(r.duration_ms)} ms</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPage={setPage} testidPrefix="refresh-logs" />
      </div>

      {keepModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={() => setKeepModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-3">نگه داشتن N رکورد آخر</h3>
            <input type="number" min={1} value={keepN} onChange={e => setKeepN(e.target.value)} className="w-full px-4 py-2 rounded-xl border border-slate-300 mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setKeepModal(false)} className="px-4 py-2 rounded-xl bg-slate-100">انصراف</button>
              <button onClick={keepLast} className="px-4 py-2 rounded-xl bg-teal-600 text-white">اعمال</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
